import type { ComponentPropsWithoutRef } from 'react';
import { Button } from '@ui/button.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PaginationData {
  current?: number;
  first?: number;
  last?: number;
  previous?: number;
  next?: number;
  totalItems?: number;
}

interface GenericPaginationProps extends ComponentPropsWithoutRef<'div'> {
  pageSize?: number;
  pageSizeOptions?: number[];
  pagination?: PaginationData;
  priceSort?: 'asc' | 'desc';
  // Alt props if pagination is not provided
  currentPage?: number;
  pageCount?: number;
  firstPage?: number;
  lastPage?: number;
  previousPage?: number;
  nextPage?: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  onPriceSortChange?: (sort: 'asc' | 'desc') => void;
}

export function GenericPagination({
  pageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  pagination,
  priceSort,
  currentPage: currentPageProp,
  pageCount: pageCountProp,
  firstPage: firstPageProp,
  lastPage: lastPageProp,
  previousPage: previousPageProp,
  nextPage: nextPageProp,
  onPageSizeChange,
  onPageChange,
  onPriceSortChange,
  className,
  ...props
}: GenericPaginationProps) {
  const { t } = useTranslation();
  const currentPage = pagination?.current ?? currentPageProp ?? 1;
  const pageCount = pagination?.totalItems
    ? Math.ceil(pagination.totalItems / pageSize)
    : (pageCountProp ?? 1);
  const firstPage = pagination?.first ?? firstPageProp;
  const lastPage = pagination?.last ?? lastPageProp;
  const previousPage = pagination?.previous ?? previousPageProp;
  const nextPage = pagination?.next ?? nextPageProp;

  return (
    <div
      className={`flex w-full flex-col-reverse items-center justify-end gap-4 overflow-auto sm:flex-row sm:gap-8 ${className}`}
      {...props}
    >
      <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8 py-1">
        <div className="hidden md:flex items-center space-x-2">
          <p className="text-sm font-medium whitespace-nowrap">{t('pagination.limit')}</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => {
              onPageSizeChange(Number(value));
              onPageChange(1);
            }}
          >
            <SelectTrigger className="h-8 w-[4.5rem]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {onPriceSortChange && priceSort && (
          <div className="hidden md:flex items-center space-x-2">
            <p className="text-sm font-medium whitespace-nowrap">{t('pagination.sort.price')}</p>
            <Select
              value={priceSort}
              onValueChange={(value: 'asc' | 'desc') => onPriceSortChange(value)}
            >
              <SelectTrigger className="h-8 w-[8rem]">
                <SelectValue placeholder={t('global.select')} />
              </SelectTrigger>
              <SelectContent side="top">
                <SelectItem value="asc">{t('pagination.sort.price.asc')}</SelectItem>
                <SelectItem value="desc">{t('pagination.sort.price.desc')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center justify-center text-sm font-medium">
          {t('pagination.info', {
            current: currentPage,
            total: pageCount
          })}
        </div>

        <div className="flex items-center space-x-2">
          <Button
            aria-label="Go to first page"
            variant="outline"
            size="custom_icon"
            className="hidden p-0 rounded-md lg:flex"
            onClick={() => onPageChange(firstPage ?? 1)}
            disabled={currentPage <= 1}
          >
            <ChevronsLeftIcon className="size-4" aria-hidden="true" />
          </Button>

          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="custom_icon"
            className="rounded-md"
            onClick={() => onPageChange(previousPage ?? Math.max(currentPage - 1, 1))}
            disabled={currentPage <= 1}
          >
            <ChevronLeftIcon className="size-4" aria-hidden="true" />
          </Button>

          <Button
            aria-label="Go to next page"
            variant="outline"
            size="custom_icon"
            className="rounded-md"
            onClick={() => onPageChange(nextPage ?? currentPage + 1)}
            disabled={currentPage >= pageCount}
          >
            <ChevronRightIcon className="size-4" aria-hidden="true" />
          </Button>

          <Button
            aria-label="Go to last page"
            variant="outline"
            size="custom_icon"
            className="hidden p-0 rounded-md lg:flex"
            onClick={() => onPageChange(lastPage ?? pageCount)}
            disabled={currentPage >= pageCount}
          >
            <ChevronsRightIcon className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
